import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import * as signals from "@/signals";
import { logIn, showDialog } from "@/utils";
import {
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { JSX, ReactNode } from "react";

interface DragAreaProps {
	children: ReactNode;
}

interface DragDataInfo {
	sortable: {
		containerId: string;
		index: number;
		items: string[];
	};
}

function DragArea({ children }: DragAreaProps): JSX.Element {
	const queryClient = useQueryClient();

	const sensors = useSensors(
		useSensor(KeyboardSensor),
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 10,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 2000,
				tolerance: 10,
			},
		}),
	);

	const handleDragCancel = (): void => {
		signals.isDragging.value = false;
	};

	const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
		// avoid triggering click event
		window.setTimeout(() => {
			handleDragCancel();
		}, 500);

		if (!signals.login.value.username) {
			await showDialog(t("shortcutsSyncWithAccountLoginFirst"), {
				showCancel: true,
			});
			logIn();
			return;
		}
		const { active, over } = event;
		if (!over?.id || active.id === over.id) {
			return;
		}

		const from = active.id.toString();
		const to = over.id.toString();

		const isFromDock = from.startsWith("dock-");
		const isFromShortcut = from.startsWith("shortcut-");
		const isToDock = to.startsWith("dock-");
		const isToShortcut = to.startsWith("shortcut-");

		const fromParts = from.split("-");
		const fromId = fromParts[1];
		const fromType = fromParts[2];

		if (isFromDock && isToDock) {
			// Dock reorder
			signals.isLoadingShown.value = true;
			try {
				const { sortable } = over.data.current as DragDataInfo;
				await new QueryDataManager(queryClient).reorderDockItem(
					fromId,
					sortable.index - 1, // accounts for "All Shortcuts" item
				);
			} catch (error) {
				void api.handleApiError(error);
			} finally {
				signals.isLoadingShown.value = false;
			}
		} else if (isFromShortcut && isToShortcut) {
			// Shortcut reorder
			signals.isLoadingShown.value = true;
			try {
				const { sortable } = over.data.current as DragDataInfo;
				await new QueryDataManager(queryClient).reorderBookmark(
					fromId,
					sortable.index,
				);
			} catch (error) {
				void api.handleApiError(error);
			} finally {
				signals.isLoadingShown.value = false;
			}
		} else if (isFromShortcut && isToDock) {
			// Add to dock
			if (fromType === "folder") {
				return;
			}
			signals.isLoadingShown.value = true;
			try {
				await new QueryDataManager(queryClient).addToDock({
					id: fromId,
					type: fromType,
				});
			} catch (error) {
				void api.handleApiError(error);
			} finally {
				signals.isLoadingShown.value = false;
			}
		}
	};

	const handleDragStart = (): void => {
		signals.isDragging.value = true;
		signals.menu.value = null; // closes context menu triggered by long press
	};

	return (
		<DndContext
			accessibility={{
				screenReaderInstructions: {
					draggable: t("keyboardDragInstructions"),
				},
			}}
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragCancel={handleDragCancel}
			onDragEnd={(event) => void handleDragEnd(event)}
			onDragStart={handleDragStart}
		>
			{children}
		</DndContext>
	);
}

export default DragArea;
